import React, {useState, useEffect} from 'react'
import axios from 'axios'
import {useMutation} from '@tanstack/react-query'
import ForgotPasswordForm from '@/components/account-drawer/login/forgotPasswordForm'
import ForgotPasswordSuccess from '@/components/account-drawer/login/forgotPasswordSuccess'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog'
import {z} from 'zod'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import AccountErrorSummary from '../account-drawer/accountErrorSummary'
import {PasswordField} from './passwordField'
import {Heading} from '@/components/ui/heading'
import {BodyCopy} from '@/components/ui/body-copy'
import {useFetchOneEntry} from '@/hooks/useFetchOneEntry'
import modelMap from 'hec/builder/model-map'

interface SetPasswordParams {
    new_password: string
    pwd_action_token: string
    user_id: string
}

const setPassword = async (params: SetPasswordParams) => {
    const {data} = await axios.post('/api/password-set', params, {
        timeout: 35000 // 35 seconds - slightly longer than server timeout
    })
    return data
}

const FormSchema = z
    .object({
        password: z
            .string({
                required_error: 'Please match the request format.'
            })
            .min(1, 'This field is required')
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
                'Password must contain 8 characters, letters, numbers, mixed case and 1 special character'
            ),
        confirm: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
    })
    .refine((data) => data.password === data.confirm, {
        message: 'Please match the request format.',
        path: ['confirm']
    })

export const ResetPasswordComponent: React.FC = () => {
    const {data: passwordResetPage} = useFetchOneEntry({
        queryKey: ['reset-password-page'],
        options: {
            model: modelMap.pageModel,
            query: {name: 'reset-password'},
            fields: 'data.url'
        }
    })
    const resetPasswordUrl = passwordResetPage?.data?.url ?? '/composable/reset-password'

    const [isSuccessful, setIsSuccessful] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [pwdActionToken, setPwdActionToken] = useState<string | null>(null)
    const [loginId, setLoginId] = useState<string | null>(null)
    //Change if needed but max 2 and like 30s delay
    const MAX_RETRIES = 2
    const MAX_RETRY_DELAY_MS = 30000
    const form = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            password: '',
            confirm: ''
        }
    })

    const isGatewayError = (error: unknown): boolean => {
        const status = (error as {response?: {status?: number}})?.response?.status
        return typeof status === 'number' && status >= 502 && status <= 504
    }

    const getExponentialBackoff = (attemptIndex: number): number => {
        return Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS)
    }

    const setPasswordMutation = useMutation({
        mutationFn: setPassword,
        retry: (failureCount, error) => {
            return isGatewayError(error) && failureCount < MAX_RETRIES
        },
        retryDelay: getExponentialBackoff,
        onError: () => {
            setError('Link expired or invalid. Please request a new password reset link.')
        }
    })

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')
        const login_id = urlParams.get('login_id')

        let base64DecodedLoginId: string | null = null
        if (login_id) {
            try {
                base64DecodedLoginId = atob(login_id)
            } catch {
                // invalid base64 — treat as missing
            }
        }
        token && login_id && base64DecodedLoginId && setPwdActionToken(token)
        base64DecodedLoginId && setLoginId(base64DecodedLoginId)
    }, [])

    if (!pwdActionToken) {
        return (
            <div className="mx-auto w-full max-w-[52.25rem] p-4 md:p-5">
                <div className="mb-6 flex flex-col items-center text-center">
                    <svg
                        className="mb-4 text-wavy"
                        width="80"
                        height="12"
                        viewBox="0 0 80 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M0 6 C10 0, 20 12, 30 6 C40 0, 50 12, 60 6 C70 0, 80 12, 80 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                        />
                    </svg>
                    <Heading variant="h2" tag="h1" data-testid="reset-password-heading">
                        Request Password Reset
                    </Heading>
                </div>
                <div className="w-full  bg-white p-6 md:p-8">
                    <Heading
                        variant="h6"
                        tag="h2"
                        data-testid="forgot-password-heading"
                        className="mb-2"
                    >
                        Forgot Your Password?
                    </Heading>
                    <BodyCopy size="small" data-testid="forgot-password-body" className="mb-6">
                        Please submit the email address associated with your account to reset your
                        password.
                    </BodyCopy>
                    <ForgotPasswordForm onSuccess={() => setIsSuccessful(true)} />
                    <Dialog open={isSuccessful} onOpenChange={setIsSuccessful}>
                        <DialogContent data-testid="forgot-password-success-dialog">
                            <DialogHeader>
                                <DialogDescription data-testid="forgot-password-success-dialog-description">
                                    <ForgotPasswordSuccess />
                                </DialogDescription>
                            </DialogHeader>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        )
    }

    const {formState} = form
    const {errors} = formState
    const isLoading = setPasswordMutation.isLoading
    const success = setPasswordMutation.isSuccess

    const onSubmit = (data: z.infer<typeof FormSchema>) => {
        setError(null)

        setPasswordMutation.mutate({
            new_password: data.password,
            pwd_action_token: pwdActionToken || '',
            user_id: loginId || ''
        })
    }

    const handleCancel = () => {
        form.reset()
        setError(null)
    }

    return (
        <div className="mx-auto w-full max-w-[52.25rem] p-4 md:p-5">
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-full rounded-lg border border-gray-300 bg-white p-6 md:p-8"
            >
                <h2 className="mb-6 mt-0 text-[1.125rem] text-blue-05 font-bold">
                    Set new password
                </h2>
                <AccountErrorSummary
                    errors={form.formState.errors}
                    data-testid={'reset-password-AccountErrorSummary'}
                />

                {/* New Password Field */}
                <PasswordField
                    className="mb-6 mt-3"
                    id={'newPassword'}
                    name={'password'}
                    label={'NEW PASSWORD'}
                    register={form.register}
                    showNewPassword={showNewPassword}
                    onToggleShow={() => setShowNewPassword((s) => !s)}
                    errorMessage={errors.password?.message}
                />

                {/* Confirm New Password Field */}
                <PasswordField
                    className="mb-6 mt-3"
                    id={'confirmPassword'}
                    name={'confirm'}
                    label={'CONFIRM NEW PASSWORD'}
                    register={form.register}
                    showNewPassword={showConfirmPassword}
                    onToggleShow={() => setShowConfirmPassword((s) => !s)}
                    errorMessage={errors.confirm?.message}
                />
                {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
                {error && (
                    <a
                        href={resetPasswordUrl}
                        className="underline"
                        data-testid="reset-password-link"
                    >
                        Request Password Reset
                    </a>
                )}
                {success && (
                    <p className="mb-4 text-sm text-green-600">Password reset successful!</p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="w-full cursor-pointer rounded border border-[#1e3a5f] bg-white px-6 py-3 text-base font-semibold text-[#1e3a5f] sm:flex-1"
                        disabled={isLoading || success}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="w-full rounded border-none bg-[#1e3a5f] px-6 py-3 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 sm:flex-1"
                        disabled={isLoading || success}
                    >
                        {isLoading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    )
}
